import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Webpack breaks Leaflet's default icon URL detection; point it at bundled assets.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [44.6488, -63.5752]; // Halifax, NS
const DEFAULT_ZOOM = 11;

const hasCoords = (cell) =>
    cell.latitude != null && cell.longitude != null &&
    Number.isFinite(Number(cell.latitude)) && Number.isFinite(Number(cell.longitude));

// Fit the map to the visible markers whenever they change.
const FitBounds = ({ positions }) => {
    const map = useMap();
    useEffect(() => {
        if (positions.length === 1) {
            map.setView(positions[0], 14);
        } else if (positions.length > 1) {
            map.fitBounds(positions, { padding: [40, 40], maxZoom: 15 });
        }
    }, [map, positions]);
    return null;
};

const CellMap = ({ cells, height = 480, onGeocodeMissing, geocoding }) => {
    const located = useMemo(() => cells.filter(hasCoords), [cells]);
    const missing = cells.length - located.length;
    const positions = useMemo(
        () => located.map((c) => [Number(c.latitude), Number(c.longitude)]),
        [located]
    );

    return (
        <div>
            {missing > 0 && (
                <div className="flex flex-col gap-2 border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 sm:flex-row sm:items-center sm:justify-between">
                    <span>
                        {missing} of {cells.length} cell{cells.length === 1 ? '' : 's'} can't be shown because the address couldn't be located.
                        Check the address and city, then save the cell again.
                    </span>
                    {onGeocodeMissing && (
                        <button
                            type="button"
                            onClick={onGeocodeMissing}
                            disabled={geocoding}
                            className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                        >
                            {geocoding ? 'Locating…' : 'Locate missing cells'}
                        </button>
                    )}
                </div>
            )}
            <MapContainer
                center={DEFAULT_CENTER}
                zoom={DEFAULT_ZOOM}
                scrollWheelZoom={false}
                style={{ height, width: '100%', zIndex: 0 }}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds positions={positions} />
                {located.map((cell) => (
                    <Marker key={cell.id} position={[Number(cell.latitude), Number(cell.longitude)]}>
                        <Popup>
                            <div className="space-y-0.5">
                                <p className="font-semibold">{cell.name}</p>
                                <p>{cell.address}{cell.city ? `, ${cell.city}` : ''}</p>
                                {cell.CellLeader?.name && <p>Leader: {cell.CellLeader.name}</p>}
                                {(cell.meeting_day || cell.meeting_time) && (
                                    <p>{[cell.meeting_day, cell.meeting_time].filter(Boolean).join(' · ')}</p>
                                )}
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>
        </div>
    );
};

export default CellMap;
